import { Link } from "@tanstack/react-router";
import { pathMap, type Locale } from "@/lib/locale";
import { getGlossaryEntryById } from "@/lib/glossary";

type Props = {
  locale: Locale;
  ids: string[];
  number?: string;
};

/**
 * Selective glossary cross-link strip for service pages.
 * Renders 3–6 themed glossary entries linked to their detail/anchor URL.
 * Placed at the bottom of service pages — outside snippet-critical zones
 * (no FAQ, hero, lead, CTA, or compact summary blocks).
 */
export function GlossaryTerms({ locale, ids, number }: Props) {
  const isEn = locale === "en";
  const base = pathMap.glossary[locale];
  const items = ids
    .map((id) => {
      const entry = getGlossaryEntryById(id);
      if (!entry) return null;
      const href = entry.tier === 1 ? `${base}/${entry.slug[locale]}` : `${base}#${entry.id}`;
      return { id, href, label: entry.term[locale], short: entry.shortDefinition[locale] };
    })
    .filter((v): v is { id: string; href: string; label: string; short: string } => Boolean(v));

  if (items.length === 0) return null;

  const heading = isEn ? "Glossary — related terms" : "Glossar — verwandte Begriffe";
  const lead = isEn
    ? "Definitions and depth on the recurring concepts of this service."
    : "Definitionen und Tiefe zu den wiederkehrenden Begriffen dieser Leistung.";

  return (
    <section className="border-t border-rule bg-white py-16 md:py-20">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        {number && <p className="eyebrow mb-3">{number} · {isEn ? "Glossary" : "Glossar"}</p>}
        <h2 className="mb-3 font-display text-2xl text-ink md:text-3xl">{heading}</h2>
        <p className="mb-7 max-w-2xl text-[15px] text-ink-3">{lead}</p>
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((it) => (
            <li key={it.id}>
              <Link
                to={it.href}
                data-glossary={it.id}
                className="group block rounded-md border border-rule bg-white p-4 transition-colors hover:border-cyan-deep/40"
              >
                <div className="font-display text-base text-ink group-hover:text-cyan-deep">
                  {it.label}
                </div>
                <div className="mt-1 line-clamp-2 text-sm text-ink-3">{it.short}</div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
